/**
 * Shared HROS API client.
 *
 * Centralises token retrieval, base URL resolution, and error handling so
 * individual pages do not duplicate fetch logic.
 *
 * Usage:
 *   import { hrosApi } from '@/lib/api';
 *   const res = await hrosApi.get('/v1/hros/employees');
 *   const res = await hrosApi.post('/v1/hros/time-logs', payload);
 */

const GATEWAY_URL = import.meta.env.VITE_GATEWAY_URL;

if (!GATEWAY_URL) {
  throw new Error('[hrosApi] VITE_GATEWAY_URL is not set. Set it in your .env file before building.');
}

const BASE_URL: string = GATEWAY_URL;

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: { code?: string; message: string };
  meta?: Record<string, unknown>;
}

function getToken(): string {
  return localStorage.getItem('axon_token') ?? '';
}

function buildHeaders(includeContentType = true): HeadersInit {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${getToken()}`,
  };
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
}

async function request<T = unknown>(
  path: string,
  method: string,
  body?: unknown,
): Promise<ApiResponse<T>> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: buildHeaders(body !== undefined),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok && res.headers.get('content-type')?.includes('application/json') === false) {
    return { success: false, error: { message: `HTTP ${res.status} ${res.statusText}` } };
  }
  return res.json() as Promise<ApiResponse<T>>;
}

export const hrosApi = {
  get: <T = unknown>(path: string) => request<T>(path, 'GET'),
  post: <T = unknown>(path: string, body?: unknown) => request<T>(path, 'POST', body),
  put: <T = unknown>(path: string, body?: unknown) => request<T>(path, 'PUT', body),
  delete: <T = unknown>(path: string) => request<T>(path, 'DELETE'),
};
